import { useState, useEffect, type ReactNode } from "react";
import { Gauge, Compass, ShieldCheck } from "lucide-react";
import { T, withAlpha } from "../tokens";

// Split-screen auth layout: a brand/value panel on the left (wide screens only)
// beside the form on the right — so auth pages use the horizontal space instead
// of a lone centered card on a big empty screen. On narrow screens the panel is
// hidden and the form centers. Shared by every auth page (sign in/up, forgot,
// reset, verify) for a consistent look.

function useIsWide(bp = 860) {
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.innerWidth >= bp);
  useEffect(() => {
    const h = () => setWide(window.innerWidth >= bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return wide;
}

const POINTS = [
  { icon: <Gauge size={15} strokeWidth={1.8}/>,       text: "Every role scored against your resume — with the reasoning shown." },
  { icon: <Compass size={15} strokeWidth={1.8}/>,     text: "India-first: remote, relocation, OCI-friendly and H1B-transfer roles." },
  { icon: <ShieldCheck size={15} strokeWidth={1.8}/>, text: "You stay in control — Caliber never applies on your behalf." },
];

function BrandPanel() {
  return (
    <div style={{
      flex: "1 1 0", minWidth: 0,
      background: T.s1, borderRight: `1px solid ${T.b0}`,
      display: "flex", flexDirection: "column", justifyContent: "center",
      padding: "48px clamp(40px, 6vw, 80px)", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "1px",
        background: `linear-gradient(90deg, transparent, ${withAlpha(T.teal, 0.44)}, transparent)` }}/>
      <div style={{ fontFamily: T.display, fontSize: "22px", fontWeight: 700, color: T.t0, marginBottom: "24px" }}>
        Caliber <span style={{ color: T.teal }}>by DevAscend</span>
      </div>
      <h2 style={{ fontFamily: T.display, fontSize: "clamp(24px, 3vw, 32px)", fontWeight: 700, color: T.t0,
        lineHeight: 1.15, letterSpacing: "-0.02em", margin: "0 0 20px", maxWidth: "420px" }}>
        Job matches that actually <span style={{ color: T.teal }}>fit</span>.
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: "14px", maxWidth: "400px" }}>
        {POINTS.map((p, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
            <span style={{ color: T.tealSoft, marginTop: "1px", flexShrink: 0 }}>{p.icon}</span>
            <span style={{ fontFamily: T.body, fontSize: "13px", color: T.t1, lineHeight: 1.5 }}>{p.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AuthShell({ children }: { children: ReactNode }) {
  const wide = useIsWide();
  return (
    <div style={{ minHeight: "100dvh", display: "flex", background: T.bg }}>
      {wide && <BrandPanel />}
      <div style={{
        flex: wide ? "1 1 480px" : "1 1 auto", minWidth: 0,
        display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 24px",
      }}>
        <div style={{ width: "100%", maxWidth: "380px" }}>{children}</div>
      </div>
    </div>
  );
}
